// @flow
import React, { Component } from "react";
import { Formik, Form } from "formik";
import Effect from "../common/FormikEffect";
import { stringify } from "querystring";
import transform from "lodash/transform";
import withStyles from "elevate-ui/withStyles";
import classNames from "classnames";

import Price from "./SimpleFilters/Price";
import Bedrooms from "./SimpleFilters/Bedrooms";
import PropertyType from "./SimpleFilters/PropertyType";
import Cities from "./SimpleFilters/Cities";

type Props = {
  classes: Object,
  filters: Object,
  query: Object,
  handleSearch: Function,
  cleanFilters: Function,
  getInitial: Function,
  mlsApiVersion: number,
};

type State = {
  availableFilters: Object,
};

class SimpleFilters extends Component<Props, State> {
  constructor(props) {
    super(props);

    const availableFilters = {
      Basic: {},
      Location: {},
      Interior: {},
      Exterior: {},
      Community: {},
      Utilities: {},
      Other: {},
    };

    props.filters &&
      props.filters.forEach((filter) => {
        // Add filters returned from the API to their appropriate sections
        if (filter.section && availableFilters[filter.section]) {
          if (!props.siteUser && !filter.requiresES) {
            // If the user isn't logged in, they should only see filters that are marked as es not-required
            availableFilters[filter.section][filter.key] = filter;
          } else if (props.siteUser) {
            // If the user is logged in, give them access to all filters
            availableFilters[filter.section][filter.key] = filter;
          }
        }
      });

    this.state = {
      availableFilters,
    };
  }

  render() {
    const {
      classes,
      cleanFilters,
      filters,
      query,
      handleSearch,
      getInitial,
    } = this.props;

    const initialFilterValues = transform(
      filters,
      (memo, filter) => {
        //If the filter has a range input then we know we need to create max min for it.
        if (!filter.createRangeInputs) {
          memo[filter.name] = getInitial({ filter, query });
        } else {
          if (this.props.mlsApiVersion === 1) {
            memo[`min${filter.name}`] = getInitial({
              filter: { ...filter, name: `min${filter.name}` },
              query,
            });
            memo[`max${filter.name}`] = getInitial({
              filter: { ...filter, name: `max${filter.name}` },
              query,
            });
          } else {
            memo[`amin_${filter.name}`] = getInitial({
              filter: { ...filter, name: `amin_${filter.name}` },
              query,
            });
            memo[`amax_${filter.name}`] = getInitial({
              filter: { ...filter, name: `amax_${filter.name}` },
              query,
            });
          }
        }
      },
      {}
    );

    return (
      <Formik
        enableReinitialize={true}
        initialValues={{
          ...initialFilterValues,
          sortName: query.sortName ? query.sortName : "",
          geoJson: query.geoJson ? query.geoJson : "",
        }}
        onSubmit={(values) => {
          handleSearch(null, cleanFilters(values));
        }}
        render={({ values }) => (
          <Form
            id="simpleForm"
            name="simpleForm"
            className={classes.form}
            method="get"
            action="/listings"
            noValidate
          >
            <Effect
              onChange={(current, next) => {
                if (
                  stringify(cleanFilters(current.values)) !==
                  stringify(cleanFilters(next.values))
                ) {
                  handleSearch(null, cleanFilters(next.values));
                }
              }}
            />
            <Cities
              values={values}
              {...this.props}
              className={classes.cities}
            />
            <Price
              values={values}
              {...this.props}
              className={classNames(classes.mLeft, classes.price)}
            />
            <Bedrooms
              values={values}
              {...this.props}
              className={classNames(classes.mLeft, classes.bedrooms)}
            />
            <PropertyType
              values={values}
              {...this.props}
              className={classNames(classes.mLeft, classes.propertyType)}
            />
          </Form>
        )}
      />
    );
  }
}

const styles = (theme) => ({
  cities: {
    marginBottom: 0,
    [theme.breakpoints(404)]: {
      marginBottom: theme.spacing.unit,
    },
  },
  form: {
    display: "flex",
    flexFlow: "row nowrap",
    justifyContent: "flex-start",
    alignItems: "center",
  },
  mLeft: {
    marginLeft: 8,
  },
  propertyType: {
    display: "none",
    [theme.breakpoints(1080)]: {
      display: "flex",
    },
  },
  bedrooms: {
    display: "none",
    [theme.breakpoints(828)]: {
      display: "flex",
    },
  },
  price: {
    display: "none",
    [theme.breakpoints(618)]: {
      display: "flex",
    },
  },
});

export default withStyles(styles, { name: "SimpleFilters" })(SimpleFilters);
